/**
 * useContagemItemActions - Handles item-level CRUD operations for ContagemCompletaPage
 *
 * Extracted from ContagemCompletaPage to keep it under 500 lines.
 * Covers: add item, delete item, import ordem, import resultado, reload table data.
 */
import { useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import contagemService from '../services/contagemService';
import { msg } from '../utils/messages';
import type { AddItemFormData } from '../components/contagem/AddItemModal';
import type { ImportOrdemResponse, ImportResultadoResponse } from '../services/contagemService';
import type { DadoContagem } from './useContagemData';

interface UseContagemItemActionsConfig {
  id: string | undefined;
  isNewContagem: boolean;
  setDadosContagem: Dispatch<SetStateAction<DadoContagem[]>>;
  setUploadResult: (result: any) => void;
  confirm: (message: string, description?: string) => Promise<boolean>;
}

export function useContagemItemActions({
  id,
  isNewContagem,
  setDadosContagem,
  setUploadResult,
  confirm,
}: UseContagemItemActionsConfig) {
  const [addingItem, setAddingItem] = useState(false);
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [showImportOrdemModal, setShowImportOrdemModal] = useState(false);
  const [showImportResultadoModal, setShowImportResultadoModal] = useState(false);

  const reloadTableData = async () => {
    if (!id || isNewContagem) return;
    const updatedResult = await contagemService.getExcelItemsEpic19(parseInt(id));
    if (updatedResult && updatedResult.itens) {
      const dadosReais = updatedResult.itens.map((item: any) =>
        contagemService.convertToUIFormat(item)
      );
      setDadosContagem(dadosReais);
      setUploadResult(updatedResult);
    }
  };

  const handleAddItem = async (formData: AddItemFormData) => {
    if (!id || isNewContagem) {
      msg.error('Salve a contagem antes de adicionar itens');
      return;
    }

    setAddingItem(true);
    try {
      const result = await contagemService.addItemManual(parseInt(id), {
        cliente_id: formData.cliente_id,
        com_regime: formData.com_regime,
        codigo_produto: formData.codigo_produto,
        descricao: formData.descricao,
        lote: formData.lote,
        data_validade: formData.data_validade,
        quantidade: formData.quantidade,
        // N46: só envia quando o usuário informou (null = comportamento antigo)
        ...(formData.quantidade_contada !== null
          ? { quantidade_contada: formData.quantidade_contada }
          : {}),
        quantidade_coletar: formData.quantidade_coletar || 0,
      });

      if (result.success) {
        msg.success(result.message);
        setShowAddItemModal(false);
        await reloadTableData();
      }
    } catch (error: any) {
      msg.error(error.message || 'Erro ao adicionar item');
      throw error;
    } finally {
      setAddingItem(false);
    }
  };

  const handleDeleteItem = async (itemId: number, codigo: string, lote: string) => {
    const confirmed = await confirm(
      `Deseja excluir o item ${codigo} - Lote ${lote}?`,
      'Esta ação não pode ser desfeita.'
    );

    if (!confirmed) return;

    if (!id || isNewContagem) {
      msg.error('Erro ao excluir item');
      return;
    }

    try {
      const result = await contagemService.deleteItem(parseInt(id), itemId);

      if (result.success) {
        msg.success(result.message);
        const updatedResult = await contagemService.getExcelItemsEpic19(parseInt(id));
        if (updatedResult && updatedResult.itens) {
          const dadosReais = updatedResult.itens.map((item: any) =>
            contagemService.convertToUIFormat(item)
          );
          setDadosContagem(dadosReais);
          setUploadResult(updatedResult);
        } else {
          setDadosContagem([]);
        }
      }
    } catch (error: any) {
      msg.error(error.message || 'Erro ao excluir item');
    }
  };

  const handleImportOrdemSuccess = async (_data: ImportOrdemResponse) => {
    await reloadTableData();
  };

  const handleImportResultadoSuccess = async (_data: ImportResultadoResponse) => {
    await reloadTableData();
  };

  return {
    addingItem,
    showAddItemModal,
    setShowAddItemModal,
    showImportOrdemModal,
    setShowImportOrdemModal,
    showImportResultadoModal,
    setShowImportResultadoModal,
    handleAddItem,
    handleDeleteItem,
    handleImportOrdemSuccess,
    handleImportResultadoSuccess,
  };
}
